import { RUNTIME_RECOVERY_FALLBACK_ID } from './runtime-recovery.constants';

export const RUNTIME_RECOVERY_UI_WINDOW_PROP: string = '__luRuntimeRecoveryUi';
export const RUNTIME_RECOVERY_FALLBACK_UI_SCRIPT_ID: string = 'lu-runtime-recovery-fallback-ui-script';

const HOST_STYLES: string = [
  'position:fixed;inset:0;z-index:2147483647;display:flex;align-items:center;',
  'justify-content:center;padding:24px;background:rgba(17,24,39,0.56);',
  'font-family:Arial,sans-serif;color:#1f2937;',
].join('');

const PANEL_STYLES: string = [
  'position:relative;width:min(420px,100%);border-radius:16px;padding:24px;',
  'background:#fff;box-shadow:0 20px 50px rgba(15,23,42,0.25);',
  'display:flex;justify-content:center;',
].join('');

const RELOAD_BUTTON_STYLES: string =
  'border:none;border-radius:8px;padding:12px 16px;background:#003c96;color:#fff;cursor:pointer;';

export interface RuntimeRecoveryUiApi {
  render: (reason: string) => void;
  renderDegraded: () => void;
}

export function getRuntimeRecoveryUiApi(windowRef: Window): RuntimeRecoveryUiApi | null {
  const rawApi: unknown = (windowRef as Window & Record<string, unknown>)[RUNTIME_RECOVERY_UI_WINDOW_PROP];
  if (!rawApi || typeof rawApi !== 'object') {
    return null;
  }

  const render: unknown = (rawApi as Record<string, unknown>).render;
  const renderDegraded: unknown = (rawApi as Record<string, unknown>).renderDegraded;
  if (typeof render !== 'function' || typeof renderDegraded !== 'function') {
    return null;
  }

  return {
    render: render as RuntimeRecoveryUiApi['render'],
    renderDegraded: renderDegraded as RuntimeRecoveryUiApi['renderDegraded'],
  };
}

export function resolveFallbackUiScriptUrl(documentRef: Document): string {
  const scripts: HTMLCollectionOf<HTMLScriptElement> = documentRef.getElementsByTagName('script');

  for (let index: number = scripts.length - 1; index >= 0; index -= 1) {
    const src: string = scripts.item(index)?.src ?? '';
    if (src.includes('runtime-recovery-startup.js')) {
      return src.split('runtime-recovery-startup.js').join('runtime-recovery-fallback-ui.js');
    }
  }

  return '/assets-ng/scripts/runtime-recovery-fallback-ui.js';
}

export function showDegradedRuntimeRecoveryFallback(windowRef: Window, documentRef: Document): void {
  const uiApi: RuntimeRecoveryUiApi | null = getRuntimeRecoveryUiApi(windowRef);
  if (uiApi !== null) {
    uiApi.renderDegraded();
    return;
  }

  if (documentRef.getElementById(RUNTIME_RECOVERY_FALLBACK_ID)) {
    return;
  }

  const host: HTMLDivElement = documentRef.createElement('div');
  host.id = RUNTIME_RECOVERY_FALLBACK_ID;
  host.setAttribute('role', 'dialog');
  host.setAttribute('aria-modal', 'true');
  host.setAttribute('data-nosnippet', '');
  host.setAttribute('style', HOST_STYLES);

  const panel: HTMLDivElement = documentRef.createElement('div');
  panel.setAttribute('data-nosnippet', '');
  panel.setAttribute('style', PANEL_STYLES);

  const reloadButton: HTMLButtonElement = documentRef.createElement('button');
  reloadButton.setAttribute('data-action', 'reload');
  reloadButton.setAttribute('style', RELOAD_BUTTON_STYLES);
  reloadButton.textContent = 'Обновить';

  function onReloadClick(): void {
    reloadButton.removeEventListener('click', onReloadClick);
    windowRef.location.reload();
  }

  reloadButton.addEventListener('click', onReloadClick);

  panel.appendChild(reloadButton);
  host.appendChild(panel);
  const container: HTMLElement = documentRef.body ?? documentRef.documentElement;
  container.appendChild(host);
}

export function loadRuntimeRecoveryFallbackUi(windowRef: Window, documentRef: Document, reason: string): void {
  const existingApi: RuntimeRecoveryUiApi | null = getRuntimeRecoveryUiApi(windowRef);
  if (existingApi !== null) {
    existingApi.render(reason);
    return;
  }

  const existingScript: HTMLElement | null = documentRef.getElementById(RUNTIME_RECOVERY_FALLBACK_UI_SCRIPT_ID);
  if (existingScript !== null) {
    bindFallbackUiScriptListeners(windowRef, documentRef, existingScript, reason);
    return;
  }

  const script: HTMLScriptElement = documentRef.createElement('script');
  script.id = RUNTIME_RECOVERY_FALLBACK_UI_SCRIPT_ID;
  script.src = resolveFallbackUiScriptUrl(documentRef);
  script.async = true;
  bindFallbackUiScriptListeners(windowRef, documentRef, script, reason);
  documentRef.head.appendChild(script);
}

function bindFallbackUiScriptListeners(
  windowRef: Window,
  documentRef: Document,
  scriptElement: HTMLElement,
  reason: string
): void {
  function onReady(): void {
    scriptElement.removeEventListener('load', onReady);
    scriptElement.removeEventListener('error', onError);
    const uiApi: RuntimeRecoveryUiApi | null = getRuntimeRecoveryUiApi(windowRef);
    if (uiApi !== null) {
      uiApi.render(reason);
      return;
    }
    showDegradedRuntimeRecoveryFallback(windowRef, documentRef);
  }

  function onError(): void {
    scriptElement.removeEventListener('load', onReady);
    scriptElement.removeEventListener('error', onError);
    showDegradedRuntimeRecoveryFallback(windowRef, documentRef);
  }

  scriptElement.addEventListener('load', onReady);
  scriptElement.addEventListener('error', onError);
}
